import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
export function WorkSnapshotOutput() {
  // In production, this would come from API based on user inputs
  const snapshotData = {
    primaryPattern: 'Over-compensation under authority pressure',
    patternDescription: 'When authority dynamics or performance pressure activates, you over-deliver or over-explain to manage perceived risk. This creates temporary relief but reinforces the pattern that your baseline output is insufficient.',
    triggerContext: 'This pattern activates most strongly in situations involving evaluation, visibility to leadership, or high-stakes deliverables.',
    nextSteps: ['This is a surface-level orientation. For deeper work pattern analysis, consider the Work Self Decode.', 'If this involves a specific person (boss, client, leader), the Work Relationship Decode maps the dynamic.', 'Pattern interruption requires recognizing the moment between pressure and response.']
  };
  return <>
      <Helmet data-id="element-4619">
        <title data-id="element-4620">Quick Decode — Work | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4621" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-4622">
        {/* Navigation */}
        <header data-id="element-4623">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" role="navigation" aria-label="Main navigation" data-id="element-4624">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-4625">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" aria-label="Return to Lazy Monk Labs home" data-id="element-4626">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-4627" />
                <span className="text-lg font-medium tracking-tight" data-id="element-4628">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-4629">
                <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-4630">
                  Decode
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" aria-label="Contact us on WhatsApp" data-id="element-4631">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        {/* Main Content */}
        <main className="pt-32 pb-24 px-6" data-id="element-4632">
          <div className="max-w-3xl mx-auto" data-id="element-4633">
            {/* Page Header */}
            <div className="mb-16" data-id="element-4634">
              <h1 className="text-4xl md:text-5xl font-medium mb-3 leading-tight tracking-tight" data-id="element-4635">
                Your Quick Decode — Work
              </h1>
              <p className="text-sm text-[#F5F5F5]/50" data-id="element-4636">
                Surface-level work pattern orientation
              </p>
            </div>

            {/* Primary Pattern */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4637">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4638">
                Primary work pattern
              </h2>
              <p className="text-xl text-[#F5F5F5]/80 mb-6 font-medium" data-id="element-4639">
                {snapshotData.primaryPattern}
              </p>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4640">
                {snapshotData.patternDescription}
              </p>
            </section>

            {/* Trigger Context */}
            <section className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-4641">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4642">
                When this activates
              </h2>
              <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-4643">
                {snapshotData.triggerContext}
              </p>
            </section>

            {/* Next Steps */}
            <section className="mb-12" data-id="element-4644">
              <h2 className="text-2xl font-medium mb-6 tracking-tight" data-id="element-4645">
                What this means
              </h2>
              <ul className="space-y-4" data-id="element-4646">
                {snapshotData.nextSteps.map((step, index) => <li key={index} className="flex gap-4" data-id="element-4647">
                    <span className="text-[#F5F5F5]/30 mt-1" data-id="element-4648">•</span>
                    <span className="text-[#F5F5F5]/70 leading-relaxed flex-1" data-id="element-4649">
                      {step}
                    </span>
                  </li>)}
              </ul>
            </section>

            {/* CTA Section */}
            <section className="border border-[#2B2B2B] p-8" data-id="element-4650">
              <h3 className="text-xl font-medium mb-4" data-id="element-4651">
                Want deeper analysis?
              </h3>
              <p className="text-[#F5F5F5]/70 mb-6 leading-relaxed" data-id="element-4652">
                This quick decode provides surface-level orientation. For
                complete work pattern mechanics, constraints, and leverage
                points, consider a full diagnostic report.
              </p>
              <Link to="/reports" className="inline-block px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-4653">
                View Full Reports
              </Link>
            </section>
          </div>
        </main>

        {/* Minimal Footer */}
        <footer className="py-8 px-6 border-t border-[#2B2B2B]" role="contentinfo" data-id="element-4654">
          <div className="max-w-7xl mx-auto text-center" data-id="element-4655">
            <p className="text-xs text-[#F5F5F5]/40" data-id="element-4656">
              © {new Date().getFullYear()} Lazy Monk Labs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
    </>;
}